// useRememberedViewport — the ReactFlow props that make a graph keep its framing.
//
// Round-2 UI note: "Graphs should remember where you were looking if you close
// them - the framing". Closing an editor unmounts the ReactFlow subtree, so the
// pan/zoom died with it and `fitView` re-framed from scratch on reopen.
//
// One hook rather than three copies of the same three props, because there are
// four canvases (body/face top level, the event graph, its function and group
// drill-ins, and the state machines) and they must agree on when to fit and when
// to restore.
//
// The contract in one line: FIT ONLY WHEN THERE IS NOTHING TO RESTORE. That is
// why the store returns null for an unknown canvas rather than a zeroed
// viewport — `fitView` and `defaultViewport` cannot both be honoured (ReactFlow
// lets fitView win), so "never framed" has to be answerable.

import { useCallback, useMemo } from 'react'
import { graphViewport } from './graphViewport'

/**
 * @param {object} opts
 * @param {string} opts.surface       'body' | 'face' | 'events'
 * @param {string|null} [opts.containerId]  the group / function / state machine
 *        this canvas shows the inside of; null for a top-level canvas. Each is a
 *        separate ReactFlow instance with its own coordinate space, so they
 *        cannot share one remembered framing.
 * @returns {{fitView: boolean, defaultViewport: object|undefined, onMoveEnd: Function}}
 */
export function useRememberedViewport({ surface, containerId = null }) {
  // Read ONCE per mount. Re-reading on every render would fight the user: the
  // canvas is the writer, and feeding its own saved value back as a prop mid-pan
  // is how a viewport ends up stuttering.
  const saved = useMemo(
    () => graphViewport.get(surface, containerId),
    [surface, containerId],
  )

  const onMoveEnd = useCallback(
    (_event, viewport) => { graphViewport.set(surface, containerId, viewport) },
    [surface, containerId],
  )

  return {
    // First open of a canvas still frames the graph, as it always did.
    fitView: !saved,
    defaultViewport: saved ?? undefined,
    // END, not move: one write when the gesture settles, so no throttle is
    // needed and nothing touches localStorage per frame.
    onMoveEnd,
  }
}
